import { QuestionIcon } from '@phosphor-icons/react'
import { typography } from '@pixee/design-system'
import React, { useCallback, useState } from 'react'
import { capitalizeFirstLetter } from '../../utils/text-utils.ts'
import { Checkbox } from '../input/checkbox'
import { InputGroup } from '../input/input-group'
import { SelectBox } from '../input/select-box.tsx'
import * as sharedStyles from '../input/shared-input.css.ts'
import { TextInput } from '../input/text-input'
import { TooltipWithHover } from '../tooltip'
import * as styles from './add-findings-drawer.css'
import * as formStyles from './workflow-form.css'

/**
 * Tools that surface a numeric severity slider instead of label checkboxes.
 * Shared between the Create and Edit workflow forms so they branch identically.
 */
export const TOOLS_SHOWING_SLIDER = ['polaris_sast', 'codeql', 'checkmarx']

// #region BranchPrefixInput

export type BranchPrefixInputProps = {
  branch: string
  setBranch: (value: string) => unknown
  helperText?: string
}

export const BranchPrefixInput = ({ branch, setBranch, helperText }: BranchPrefixInputProps) => {
  const [isTouched, setIsTouched] = useState(false)
  const hasTrailingSlashError = branch.endsWith('/')
  const showError = isTouched && hasTrailingSlashError

  return (
    <div className={styles.flexContainer}>
      <TextInput
        id="branch"
        name="branch"
        label="Branch / Prefix"
        value={branch}
        onChange={setBranch}
        onBlur={() => setIsTouched(true)}
        placeholder="Enter a branch name or prefix pattern (requires *)"
        explicitLabelCondition="optional"
        errorText={showError ? "A trailing '*' is required for prefix pattern matching" : undefined}
        isError={showError}
      />
      {!showError && (
        <div className={formStyles.branchHelperRow}>
          <span>
            {helperText ??
              'Exact match by default. Use * to match multiple branches. Default branch will be used if nothing is specified.'}
          </span>
          <TooltipWithHover
            side="top"
            trigger={
              <span className={formStyles.branchTooltipIcon} aria-label="Branch pattern examples">
                <QuestionIcon size={14} weight="light" />
              </span>
            }
          >
            <div className={formStyles.branchTooltipContent}>
              <div>
                <strong>main</strong> <em>(exact match; indicates a single branch)</em>
              </div>
              <div>
                <strong>feature/*</strong> <em>(prefix; indicates multiple branches)</em>
              </div>
            </div>
          </TooltipWithHover>
        </div>
      )}
    </div>
  )
}

// #endregion

// #region SeveritySelector

// TODO: Move to common location.
export type Severity = {
  label: string
}

export type SeveritySelectorProps = {
  severities?: Severity[]
  selectedSeverities: string[]
  setSelectedSeverities: React.Dispatch<React.SetStateAction<string[]>>
}

export const SeveritySelector = ({ severities, selectedSeverities, setSelectedSeverities }: SeveritySelectorProps) => {
  if (!severities?.length) {
    return null
  }

  return (
    <div className={formStyles.severityContainer}>
      <div className={sharedStyles.labelContainer}>
        <label className={sharedStyles.inputLabel}>Severity</label>
        <span className={sharedStyles.optionalText}>(Optional)</span>
      </div>
      <div className={formStyles.checkboxGroup}>
        {severities.map(severity => (
          <div key={severity.label} className={formStyles.severityItem}>
            <Checkbox
              id={severity.label.toLowerCase()}
              checked={selectedSeverities.includes(severity.label.toLowerCase())}
              onCheckedChange={checked => {
                setSelectedSeverities(prev =>
                  checked
                    ? [...prev, severity.label.toLowerCase()]
                    : prev.filter(s => s !== severity.label.toLowerCase())
                )
              }}
              state="default"
            />
            <label
              htmlFor={severity.label.toLowerCase()}
              className={`${typography.paragraph} ${formStyles.severityLabel}`}
            >
              {capitalizeFirstLetter(severity.label)}
            </label>
          </div>
        ))}
      </div>
    </div>
  )
}

// #endregion

// #region FixesCustomizationSelector

export type FixConfidence = 'high' | 'medium' | 'low'

export type FixesCustomizationSelectorProps = {
  severitySection?: React.ReactNode
  fixConfidence: FixConfidence | undefined
  setFixConfidence: React.Dispatch<React.SetStateAction<FixConfidence | undefined>>
  limitFixes: boolean
  setLimitFixes: React.Dispatch<React.SetStateAction<boolean>>
  fixesLimit: number
  setFixesLimit: React.Dispatch<React.SetStateAction<number>>
}

export function isFixConfidence(value: string | undefined): value is FixConfidence {
  return value !== undefined && ['high', 'medium', 'low'].includes(value)
}

export const FixesCustomizationSelector = ({
  severitySection,
  fixConfidence,
  setFixConfidence,
  limitFixes,
  setLimitFixes,
  fixesLimit,
  setFixesLimit,
}: FixesCustomizationSelectorProps) => {
  const onValueChange = useCallback(
    (value: string) => {
      if (value === undefined || isFixConfidence(value)) {
        setFixConfidence(value)
      } else {
        console.warn(`Invalid fix confidence value: ${value}`)
      }
    },
    [setFixConfidence]
  )

  return (
    <InputGroup>
      <div className={formStyles.sectionGroup}>
        {severitySection}
        <SelectBox
          label="Minimum fix confidence"
          explicitLabelCondition="optional"
          placeholder="Select confidence level"
          value={fixConfidence}
          onValueChange={onValueChange}
          options={
            [
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' },
            ] satisfies { value: FixConfidence; label: string }[]
          }
        />

        <div className={formStyles.limitFixesRow}>
          <div className={formStyles.limitFixesCheckbox}>
            <Checkbox
              id="limit-fixes"
              checked={limitFixes}
              onCheckedChange={(checked: boolean | 'indeterminate') => setLimitFixes(checked === true)}
              state="default"
            />
            <label htmlFor="limit-fixes" className={formStyles.limitFixesLabel}>
              Limit the number of fixes per pull request
            </label>
          </div>
          <TextInput
            className={limitFixes ? undefined : formStyles.hidden}
            type="number"
            value={fixesLimit.toString()}
            onChange={value => setFixesLimit(Math.max(1, parseInt(value) || 1))}
            width={80}
          />
        </div>
      </div>
    </InputGroup>
  )
}

// #endregion
